import { useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { PackageIcon, PlusIcon } from 'lucide-react'

import { QUOTA_KEYS, QUOTAS } from '@ems/contracts/plan-quotas'
import { Button } from '@ems/ui/components/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ems/ui/components/card'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { sessionQuery } from '#admin/features/auth/queries.ts'
import { hasPlatformScope } from '#admin/features/platform/api.ts'
import { platformModuleLabel } from '#admin/features/platform/modules.ts'
import { NewPlanDialog } from '#admin/features/platform/plans/new-plan-dialog.tsx'
import { plansQuery } from '#admin/features/platform/plans/queries.ts'
import { describeQuota } from '#admin/features/platform/plans/quota-fields.ts'
import { PlansSkeleton } from '#admin/features/platform/platform-skeletons.tsx'

/** `/platform/plans` — what editions can be sold: modules, levels and quotas. */
export const Route = createFileRoute('/_authenticated/platform/plans/')({
  pendingComponent: PlansSkeleton,
  loader: ({ context }) => context.queryClient.ensureQueryData(plansQuery()),
  component: PlansPage,
})

const CORE = new Set(['identity', 'dashboard'])

function PlansPage() {
  const { data: plans } = useSuspenseQuery(plansQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const canManage = hasPlatformScope(session, 'platform:plans.manage')

  return (
    <>
      <PageHeader
        title="Plans"
        description="A plan decides which modules an edition holds, at which level, and its quotas. Editing one rolls out to every edition on it."
        actions={
          canManage ? (
            <Button onClick={() => setOpen(true)}>
              <PlusIcon />
              New plan
            </Button>
          ) : null
        }
      />

      {plans.items.length === 0 ? (
        <EmptyState icon={<PackageIcon />} title="No plans" description="Create the first plan." />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {plans.items.map((plan) => {
            const granted = Object.entries(plan.modules).filter(
              ([key, level]) => level !== 'none' && !CORE.has(key),
            )
            return (
              <li key={plan.id}>
                <Link
                  to="/platform/plans/$planId"
                  params={{ planId: plan.id }}
                  className="focus-visible:ring-ring/50 block h-full rounded-xl outline-none focus-visible:ring-[3px]"
                >
                  <Card className="hover:border-primary/50 h-full transition-colors">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle>{plan.name}</CardTitle>
                        {plan.isActive ? null : <StatusBadge tone="neutral">inactive</StatusBadge>}
                      </div>
                      <CardDescription>
                        <span className="font-mono">{plan.key}</span> · {plan.tenantCount} edition
                        {plan.tenantCount === 1 ? '' : 's'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3">
                      <ul className="flex flex-wrap gap-1.5" aria-label="Modules">
                        {granted.length === 0 ? (
                          <li className="text-muted-foreground text-xs">Core modules only</li>
                        ) : (
                          granted.map(([key, level]) => (
                            <li key={key}>
                              <StatusBadge tone={level === 'full' ? 'success' : 'warning'}>
                                {platformModuleLabel(key)}
                                {level === 'read_only' ? ' · read-only' : ''}
                              </StatusBadge>
                            </li>
                          ))
                        )}
                      </ul>
                      <dl className="text-muted-foreground grid gap-y-1 text-xs">
                        {QUOTA_KEYS.map((key) => (
                          <div key={key} className="flex justify-between gap-2">
                            <dt>{QUOTAS[key].label}</dt>
                            <dd className="text-foreground tabular-nums">
                              {describeQuota(key, plan.quotas[key])}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </CardContent>
                  </Card>
                </Link>
              </li>
            )
          })}
        </ul>
      )}

      {canManage ? (
        <NewPlanDialog
          open={open}
          onOpenChange={setOpen}
          plans={plans.items}
          onCreated={(plan) => {
            setOpen(false)
            void navigate({ to: '/platform/plans/$planId', params: { planId: plan.id } })
          }}
        />
      ) : null}
    </>
  )
}
